export const NOT_STATED = 'not stated';
export const usd = (amount)=>`$${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;
export const plural = (n, one, many = `${one}s`)=>`${n} ${n === 1 ? one : many}`;
/** "BS + 7 yrs" from the parsed fields; null when neither was parsed. */ export function formatQuals(education, years) {
    const parts = [
        education,
        years >= 0 ? `${years} yrs` : ''
    ].filter(Boolean);
    return parts.length ? parts.join(' + ') : null;
}
/** Parsed quals, else the raw text truncated to 40, else null (rendered "not stated"). */ export function qualsText(row) {
    const parsed = formatQuals(row.parsedEducationLevel, row.parsedMinExperienceYears);
    if (parsed) return parsed;
    const raw = row.qualificationsText.trim();
    if (!raw) return null;
    return raw.length > 40 ? `${raw.slice(0, 40)}…` : raw;
}
export function mappingLabel(status, relation) {
    if (status !== 'APPROVED') return 'Needs review';
    if (relation === 'BROADER') return 'Broader';
    if (relation === 'NARROWER') return 'Narrower';
    return 'Equivalent';
}
export function monthYear(date) {
    return date.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC'
    });
}
/** What the server labels a mapping proposed by 'system:lcat-suggester' (REG-50). */ export const SUGGESTER_LABEL = 'Mapping suggester';
/** "Mapping suggester · 0.82"; shared by the versions table and the review queue. */ export const suggesterText = (confidence)=>`${SUGGESTER_LABEL} · ${confidence.toFixed(2)}`;
export function mappingNote(row) {
    if (row.mappingStatus === 'APPROVED' && row.decidedByName) {
        return row.decidedAt ? `${row.decidedByName} · ${monthYear(row.decidedAt.toDate())}` : row.decidedByName;
    }
    if (row.proposedByLabel === SUGGESTER_LABEL) return suggesterText(row.confidence);
    if (row.proposedByLabel) return `proposed by ${row.proposedByLabel}`;
    if (row.solicitationRef) return `as written in ${row.solicitationRef}`;
    return '';
}
export function companyLabel(row) {
    if (row.companyIsOwn) return 'Us';
    if (row.scopeKind === 'SOLICITATION') return `${row.issuingOrg?.name ?? 'Solicitation'} (solicitation)`;
    return row.company?.name ?? 'Company not recorded';
}
export function vehicleLabel(row) {
    const vehicle = row.vehicle?.name ?? '';
    if (row.scopeKind === 'SOLICITATION') return [
        row.solicitationRef,
        vehicle
    ].filter(Boolean).join(' · ');
    return vehicle;
}
const EDU_TEXT = {
    ABOVE: 'above anchor',
    BELOW: 'below anchor'
};
export function comparisonRows(row) {
    const c = row.comparison;
    const edu = c?.education ?? 'UNKNOWN';
    const delta = c?.experienceDeltaYears ?? 0;
    const hasExp = c?.hasExperience ?? false;
    const level = row.parsedEducationLevel;
    const eduText = edu === 'SAME' ? `${level ? `${level} = ` : 'same as '}anchor` : edu === 'UNKNOWN' ? NOT_STATED : `${level ? `${level}, ` : ''}${EDU_TEXT[edu] ?? 'differs from anchor'}`;
    const years = row.parsedMinExperienceYears;
    const expText = !hasExp ? NOT_STATED : `${years} yrs, ${delta === 0 ? 'same as typical' : delta > 0 ? 'above typical' : 'below typical'}`;
    return [
        {
            id: 'edu',
            label: 'Education',
            value: eduText,
            state: edu === 'SAME' ? 'same' : edu === 'UNKNOWN' ? 'neutral' : 'different'
        },
        {
            id: 'exp',
            label: 'Experience',
            value: expText,
            state: !hasExp ? 'neutral' : delta === 0 ? 'same' : 'different'
        },
        {
            id: 'clr',
            label: 'Clearance',
            value: c?.clearanceText || NOT_STATED,
            state: 'neutral'
        }
    ];
}
/** Marker shape is the identity channel; tone follows the spec's mapping. */ export const POINT_TYPES = {
    CEILING: {
        label: 'ceiling (posted)',
        shape: 'ring',
        tone: 'theirs'
    },
    SUBK_AGREED: {
        label: 'agreed in a subcontract',
        shape: 'dot',
        tone: 'good'
    },
    BILLED: {
        label: 'billed',
        shape: 'square',
        tone: 'series'
    },
    AWARDED: {
        label: 'awarded',
        shape: 'square',
        tone: 'cat-1'
    },
    PROPOSED: {
        label: 'proposed',
        shape: 'triangle',
        tone: 'cat-6'
    },
    INFERRED: {
        label: 'inferred (estimate)',
        shape: 'dashed-ring',
        tone: 'pending'
    }
};
export const PointTypeOrder = [
    'CEILING',
    'SUBK_AGREED',
    'BILLED',
    'PROPOSED',
    'INFERRED'
];
export const PINNED_LEGEND_LABELS = [
    'ceiling (posted)',
    'agreed in a subcontract',
    'billed',
    'proposed',
    'inferred (estimate)',
    'CALC+ median',
    'suggested for pricing'
];
export const pointStyle = (type)=>POINT_TYPES[type] ?? {
        label: type.toLowerCase() || 'rate',
        shape: 'dot',
        tone: 'other'
    };
export const sourceText = (p)=>p.sourceLabel || 'source not recorded';
/** "{type label} ${amount} · {period_label} · {source_label}" — every point keeps its source. */ export function pointTitle(p) {
    return [
        `${pointStyle(p.observationType).label} ${usd(p.amount)}`,
        p.periodLabel || 'period not stated',
        sourceText(p)
    ].join(' · ');
}
const relationText = (status, relation)=>status === 'APPROVED' ? relation.toLowerCase() || 'equivalent' : 'needs review';
export function plotRows(res) {
    return res.lanes.map((lane, i)=>{
        const ceilings = lane.points.filter((p)=>p.observationType === 'CEILING');
        const real = lane.points.filter((p)=>p.observationType !== 'CEILING');
        const note = [
            lane.vehicle?.name,
            relationText(lane.mappingStatus, lane.relation) + (lane.usedForSuggestion ? '' : ' · not in the band')
        ].filter(Boolean).join(' · ');
        return {
            id: `${lane.company?.id ?? 'lane'}:${lane.vehicle?.id ?? ''}:${i}`,
            label: lane.company?.name ?? 'Company not recorded',
            sublabel: note,
            points: lane.points.map((p)=>{
                const s = pointStyle(p.observationType);
                return {
                    id: p.observationId,
                    value: p.amount,
                    shape: s.shape,
                    tone: s.tone,
                    title: pointTitle(p)
                };
            }),
            span: lane.hasGap && ceilings.length && real.length ? {
                from: Math.min(...real.map((p)=>p.amount)),
                to: Math.max(...ceilings.map((p)=>p.amount)),
                label: `−${Math.abs(Math.round(lane.gapPercent))}% off ceiling`
            } : undefined
        };
    });
}
/** Axis: floor10(min) − 10 to ceil10(max) + 10, a tick every $20. */ export function plotAxis(values) {
    if (values.length === 0) return {
        domain: [
            0,
            100
        ],
        ticks: [
            0,
            20,
            40,
            60,
            80,
            100
        ]
    };
    const lo = Math.floor(Math.min(...values) / 10) * 10 - 10;
    const hi = Math.ceil(Math.max(...values) / 10) * 10 + 10;
    const ticks = [];
    for(let t = lo; t <= hi; t += 20)ticks.push(t);
    return {
        domain: [
            lo,
            hi
        ],
        ticks
    };
}
/** "YYYY–now" for a person's seat on the program. */ export function seatYears(p) {
    const start = p.startDate?.toDate().getUTCFullYear();
    if (!start) return '';
    const end = p.endDate?.toDate().getUTCFullYear();
    return `${start}–${end ?? 'now'}`;
}
